<template>
  <div class="noticia-detail-container">
    <!-- Header transparente sobre la imagen -->
    <div class="detail-header" :class="{ scrolled: isScrolled }">
      <div class="header-left">
        <button class="btn-back" @click="goBack">
          <i class="material-icons">arrow_back</i>
        </button>
        <div class="header-actions">
          <button class="action-icon share-icon" @click="shareNative">
            <i class="material-icons">share</i>
          </button>
          <button
            class="action-icon fav-icon"
            @click="toggleFavorite"
            :disabled="favoriteLoading"
          >
            <i class="material-icons" :class="{ favorited: isFavorite }">
              {{ isFavorite ? "favorite" : "favorite_border" }}
            </i>
          </button>
        </div>
      </div>
      <div class="header-right">
        <img
          :src="faviconUrl"
          alt="JLHVISION"
          class="header-favicon"
          onerror="this.src='favicon.ico'"
        />
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-if="isLoading" class="skeleton-wrapper">
      <div class="skeleton skeleton-image"></div>
      <div class="skeleton-content">
        <div class="skeleton skeleton-title"></div>
        <div class="skeleton skeleton-text"></div>
        <div class="skeleton skeleton-text"></div>
        <div class="skeleton skeleton-text"></div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="error-container">
      <i class="material-icons">error_outline</i>
      <p>{{ error }}</p>
      <button class="btn-retry" @click="loadPost">Reintentar</button>
    </div>

    <!-- Contenido de la noticia -->
    <div v-if="!isLoading && !error && post" class="noticia-content">
      <!-- Imagen destacada (ocupa la mitad de la pantalla) -->
      <div class="post-image-container">
        <img
          :src="getImageUrl(post.image)"
          :alt="post.title"
          class="post-image"
          @error="handleImageError"
        />
        <div class="image-gradient"></div>

        <!-- Contenido superpuesto sobre la imagen -->
        <div class="image-content">
          <span class="post-category">{{
            post.category?.name || "General"
          }}</span>
          <h1 class="post-title">{{ post.title }}</h1>
        </div>

        <div class="post-badge" v-if="post.is_express">
          <i class="fas fa-crown"></i> PREMIUM
        </div>
      </div>

      <!-- Contenido del post (con bordes redondos y superpuesto) -->
      <div class="post-content-wrapper">
        <div class="post-content">
          <div class="post-meta-top">
            <span class="post-date">{{ formatDate(post.created_at) }}</span>
          </div>

          <p class="post-extract">{{ post.extract }}</p>

          <!-- Cuerpo del post -->
          <div class="post-body" v-html="safeBody"></div>

          <!-- Tags -->
          <div class="post-tags" v-if="post.tags && post.tags.length > 0">
            <span class="tags-title">Etiquetas:</span>
            <div class="tags-list">
              <span class="tag" v-for="tag in post.tags" :key="tag.id">
                #{{ tag.name }}
              </span>
            </div>
          </div>

          <!-- Información del autor -->
          <div class="post-author" v-if="post.user">
            <img
              :src="getAvatarUrl(post.user.avatar)"
              :alt="post.user.full_name"
              class="author-avatar"
            />
            <div class="author-info">
              <span class="author-name">{{ post.user.full_name }}</span>
              <span class="author-designation" v-if="post.user.designation"
                >{{ post.user.designation }} - JLHVISION</span
              >
            </div>
          </div>

          <!-- Posts similares -->
          <div class="similars-section" v-if="similars.length > 0">
            <h3 class="similars-title">Te puede interesar</h3>
            <div class="similars-list">
              <router-link
                v-for="similar in similars"
                :key="similar.id"
                :to="`/noticia/${similar.slug}`"
                class="similar-item"
              >
                <img
                  :src="getImageUrl(similar.image)"
                  :alt="similar.title"
                  class="similar-image"
                />

                <div class="similar-info">
                  <h6 class="similar-category">
                    {{ similar.category?.name }}
                  </h6>

                  <h4>{{ similar.title }}</h4>

                  <span class="similar-date">
                    {{ formatDate(similar.created_at) }}
                  </span>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast de éxito -->
    <div class="toast-success" v-if="showCopySuccess">
      <i class="material-icons">check_circle</i>
      <span>Enlace copiado</span>
    </div>

    <!-- Modal premium -->
    <div
      class="premium-modal-overlay"
      v-if="showPremiumModal"
      @click="closePremiumModal"
    >
      <div class="premium-modal" @click.stop>
        <div class="modal-icon">
          <i class="material-icons">lock</i>
        </div>
        <h2>Debes ser premium</h2>
        <p>Para guardar esta noticia en favoritos</p>
        <button class="btn-premium-modal" @click="closePremiumModal">
          Cerrar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "../../services/api";
import { useAuthStore } from "../../stores/auth";
import { watch } from 'vue'

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const post = ref(null);
const similars = ref([]);
const isLoading = ref(true);
const error = ref(null);
const safeBody = ref("");
const faviconUrl = ref("/favicon.ico");
const isFavorite = ref(false);
const favoriteLoading = ref(false);
const showCopySuccess = ref(false);
const showPremiumModal = ref(false);
const isScrolled = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 200;
};

const loadPost = async () => {
  isLoading.value = true;
  error.value = null;

  const slug = route.params.slug;

  try {
    const response = await api.get(`/premium/${slug}`);
    post.value = response.data.post;
    similars.value = response.data.similars || [];

    safeBody.value = post.value.body;

    await checkFavoriteStatus();
    await loadFavicon();
  } catch (err) {
    console.error("Error al cargar la noticia:", err);
    error.value = err.response?.data?.message || "No se pudo cargar la noticia";
  } finally {
    isLoading.value = false;
  }
};

const loadFavicon = async () => {
  try {
    const response = await api.get("/settings");
    if (response.data.setting?.site_favicon) {
      faviconUrl.value = `https://portal.jlhvision.com/uploads/${response.data.setting.site_favicon}`;
    }
  } catch (err) {
    console.error("Error al cargar favicon:", err);
  }
};

const checkFavoriteStatus = async () => {
  const user = authStore.user;

  if (!user || !post.value) return;

  try {
    const response = await api.get(`/favorites/check/${post.value.id}`);
    isFavorite.value = response.data.is_favorite;
  } catch (err) {
    console.error("Error al verificar favorito:", err);
  }
};

const toggleFavorite = async () => {
  const user = authStore.user;

  console.log("USER:", user);

  // 🔐 SOLO is_premium (igual que backend)
  if (!user || !user.is_premium) {
    showPremiumModal.value = true;
    return;
  }

  favoriteLoading.value = true;

  try {
    if (isFavorite.value) {
      await api.delete(`/favorites/${post.value.id}`);
      isFavorite.value = false;
    } else {
      await api.post(`/favorites/${post.value.id}`);
      isFavorite.value = true;
    }
  } catch (err) {
    // 🔥 respaldo backend
    if (err.response?.data?.requires_premium) {
      showPremiumModal.value = true;
      return;
    }

    console.error(err);
  } finally {
    favoriteLoading.value = false;
  }
};

const shareNative = () => {
  if (navigator.share) {
    navigator
      .share({
        title: post.value?.title || "JLHVISION",
        text: post.value?.extract || "Comparte esta noticia",
        url: window.location.href,
      })
      .catch(() => {});
  } else {
    navigator.clipboard.writeText(window.location.href);
    showCopySuccess.value = true;
    setTimeout(() => (showCopySuccess.value = false), 2000);
  }
};

const getImageUrl = (image) => {
  if (!image) return "/default-image.jpg";
  if (image.startsWith("http")) return image;
  return `https://portal.jlhvision.com/posts/${image}`;
};

const getAvatarUrl = (avatar) => {
  if (!avatar) return "/default-avatar.png";
  if (avatar.startsWith("http")) return avatar;
  return `https://portal.jlhvision.com/avatars/${avatar}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const goBack = () => {
  router.back();
};


const closePremiumModal = () => {
  showPremiumModal.value = false;
};

const handleImageError = (event) => {
  event.target.src = "/default-image.jpg";
};

watch(() => route.params.slug, () => {
  loadPost()
})

onMounted(() => {
  loadPost();
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.noticia-detail-container {
  min-height: 100vh;
  background: #f5f5f5;
}

/* Header transparente sobre la imagen */
.detail-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: transparent;
  z-index: 100;
  transition: all 0.3s ease;
}

.detail-header.scrolled {
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.detail-header.scrolled .btn-back i,
.detail-header.scrolled .action-icon i {
  color: #00144a;
}

.detail-header.scrolled .header-favicon {
  filter: none;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.btn-back i {
  font-size: 24px;
  color: white;
}

.btn-back:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: scale(1.05);
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-icon {
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.action-icon i {
  font-size: 22px;
  color: white;
}

.action-icon:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: scale(1.05);
}

.action-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fav-icon i.favorited {
  color: #d12121;
}

.header-right {
  display: flex;
  align-items: center;
}

.header-favicon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

/* Imagen destacada - ocupa la mitad de la pantalla */
.post-image-container {
  position: relative;
  width: 100%;
  height: 50vh;
  min-height: 360px;
  overflow: hidden;
}

.post-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.image-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.4) 50%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
}

.image-content {
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  padding: 30px 20px;
  z-index: 5;
}

.image-content .post-category {
  display: inline-block;
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 12px;
  border-radius: 20px;
}

.image-content .post-title {
  font-size: 28px;
  font-weight: bold;
  color: white;
  margin: 0;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.post-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: linear-gradient(135deg, #f5af19 0%, #f12711 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 10;
}

/* Contenido del post - con bordes redondos y superpuesto */
.post-content-wrapper {
  position: relative;
  margin-top: -30px;
  z-index: 10;
}

.post-content {
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 24px 20px;
}

.post-meta-top {
  margin-bottom: 15px;
}

.post-date {
  color: #999;
  font-size: 13px;
  margin-left: 20px;
}

.post-extract {
  font-size: 16px;
  font-style: italic;
  color: #555;
  margin: 0 0 20px;
  line-height: 1.5;
  padding: 15px;
  background: #f9f9f9;
  border-left: 4px solid #a50000;
  border-radius: 0 8px 8px 0;
}

/* Cuerpo del post */
.post-body {
  font-size: 16px;
  line-height: 1.7;
  color: #333;
  text-align: justify;
}

.post-body img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 20px 0;
}

.post-body p {
  margin-bottom: 20px;
}

.post-body h2,
.post-body h3 {
  color: #00144a;
  margin-top: 30px;
  margin-bottom: 15px;
}

/* Tags */
.post-tags {
  margin: 30px 0;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.tags-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.tag {
  background: #f5f5f5;
  color: #00144a;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

/* Autor */
.post-author {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 12px;
  margin: 20px 0;
}

.author-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.author-info {
  display: flex;
  flex-direction: column;
}

.author-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.author-designation {
  font-size: 12px;
  color: #666;
}

/* Posts similares */
.similars-section {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.similars-title {
  font-size: 18px;
  font-weight: bold;
  color: #00144a;
  margin-bottom: 20px;
  margin-left: 20px;
}

.similars-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.similar-item {
  display: flex;
  gap: 12px;
  cursor: pointer;
  padding: 12px;
  border-radius: 12px;
  transition: all 0.3s;
  text-decoration: none;
}

.similar-item:hover {
  background: #f0f0f0;
  transform: translateX(4px);
  text-decoration: none;
}

.similar-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
}

.similar-info {
  flex: 1;
}

.similar-category {
  color: #a50000;
}

.similar-info h4 {
  font-size: 14px;
  font-weight: 600;
  color: #001b4a;
  margin: 0 0 5px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
}

.similar-date {
  font-size: 12px;
  color: #999;
}

/* Skeleton */
.skeleton-wrapper {
  padding: 20px;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

.skeleton-image {
  width: 100%;
  height: 300px;
}

.skeleton-content {
  margin-top: 20px;
}

.skeleton-title {
  width: 80%;
  height: 28px;
  margin-bottom: 15px;
}

.skeleton-text {
  width: 100%;
  height: 16px;
  margin-bottom: 12px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Error */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  min-height: 100vh;
}

.error-container i {
  font-size: 48px;
  color: #f12711;
  margin-bottom: 20px;
}

.error-container p {
  color: #666;
  margin-bottom: 20px;
}

.btn-retry {
  background: #00144a;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 25px;
  cursor: pointer;
}

/* Toast */
.toast-success {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: #4caf50;
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1000;
  font-size: 14px;
}

/* Modal premium */
.premium-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.premium-modal {
  background: white;
  padding: 30px;
  border-radius: 20px;
  text-align: center;
  max-width: 300px;
  width: 85%;
}

.modal-icon {
  background: #eef2f7;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
}

.modal-icon i {
  font-size: 36px;
  color: #00144a;
}

.premium-modal h2 {
  margin: 10px 0;
  font-size: 20px;
  color: #222;
}

.premium-modal p {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}

.btn-premium-modal {
  width: 100%;
  padding: 12px;
  background: #00144a;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* Responsive */
@media (max-width: 768px) {
  .post-image-container {
    height: 45vh;
  }

  .image-content .post-title {
    font-size: 22px;
  }

  .image-content .post-category {
    font-size: 11px;
  }

  .post-content {
    padding: 20px 16px;
  }

  .post-extract {
    font-size: 14px;
  }

  .header-left {
    gap: 10px;
  }

  .btn-back,
  .action-icon {
    width: 36px;
    height: 36px;
  }

  .btn-back i,
  .action-icon i {
    font-size: 20px;
  }

  .post-body {
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .post-image-container {
    height: 40vh;
  }

  .image-content .post-title {
    font-size: 20px;
  }

  .image-content {
    padding: 20px 16px;
  }

  .post-content {
    padding: 16px 12px;
  }
}
</style>
